import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { useSelector, useDispatch } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from '@ant-design/compatible';
import { Input, Button } from 'antd';
import { makeStyles } from '@material-ui/styles';
import jwtDecode from 'jwt-decode';
import { pageType } from '~constants/pages';
import event from '~helpers/mixpanel';
import generatePath from '~client/settings/routing';
import { setPassword as setPasswordAction } from '~client/actions/auth';
import { communicationStatus } from '~client/constants';

const useStyles = makeStyles(theme => ({
  wrapper: {
    '& .ant-legacy-form-item': {
      marginBottom: 20,
    },
  },
  title: {
    ...theme.typography.h1,
    color: theme.colors.gray9,
    marginBottom: 12,
  },
  heading: {
    ...theme.typography.h5,
    fontWeight: 'normal',
    color: theme.colors.gray7,
    marginBottom: 40,
  },
  submitBtn: {
    ...theme.typography.h5,
    marginBottom: 21,
  },
  backBtn: {
    ...theme.typography.h5,
    fontWeight: 'normal',
    textAlign: 'center',

    '& a:hover': {
      color: theme.colors.mainGreen6,
    },
  },
}));

const ResetPassword = ({ location, form }) => {
  const query = queryString.parse(location.search);

  const token = query['reset-token'] ?? null;
  const { email } = jwtDecode(token) ?? {};

  const { getFieldDecorator, validateFields } = form;

  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { communication } = useSelector(state => ({
    communication: state.auth.getIn(['setPassword', 'communication']).toJS(),
  }));

  const handleSetPassword = e => {
    e.preventDefault();

    validateFields((err, values) => {
      if (!err) {
        dispatch(
          setPasswordAction({
            token: token,
            newPassword: values.newPassword,
            portal: 'LANDLORD',
          }),
        );
      }
    });
  };

  useEffect(() => {
    event('Viewed Reset Password Page', { 'Current Page Type': pageType.resetPasswordPage });
  }, []);

  if (!token) {
    return <Redirect to={generatePath('login')} />;
  }

  return (
    <div className={classes.wrapper}>
      <h1 className={classes.title}>{t('landlord.set_password.main.title')}</h1>

      <h2 className={classes.heading}>
        {t('landlord.set_password.main.subTitle', {
          email,
        })}
      </h2>

      <Form onSubmit={handleSetPassword} className="auth__main__form">
        <Form.Item>
          {getFieldDecorator('newPassword', {
            validateTrigger: 'onBlur',
            initialValue: '',
            rules: [
              {
                required: true,
                message: t('landlord.auth.reset_password.blank_error.new_password'),
              },
              {
                min: 8,
                message: t('landlord.auth.reset_password.format_error.new_password'),
              },
            ],
          })(
            <Input
              type="password"
              size="large"
              placeholder={t('landlord.set_password.main.new_password.label')}
            />,
          )}
        </Form.Item>

        <Button
          block
          type="primary"
          size="large"
          htmlType="submit"
          className={classes.submitBtn}
          disabled={communication.status === communicationStatus.FETCHING}
        >
          {t('landlord.set_password.main.button.reset_password')}
        </Button>

        <div className={classes.backBtn}>
          <Link to={generatePath('login')}>{t('landlord.set_password.main.go_to_login')}</Link>
        </div>
      </Form>
    </div>
  );
};

ResetPassword.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    validateFields: PropTypes.func.isRequired,
  }),
};

ResetPassword.defaultProps = {
  location: {
    search: '',
  },
  form: PropTypes.shape({
    getFieldDecorator: () => {},
    validateFields: () => {},
  }),
};

export default Form.create({
  name: 'ResetPassword',
})(ResetPassword);
